import React from "react";
import { Typography } from "antd";
import { Tokens } from "Shared/Tokens";

const { Text } = Typography;

const BAR_PALETTE = [
    Tokens.accent,
    "#d4d4d8",
    "#a1a1a6",
    "#71717a",
    "#52525b",
    "#3f3f46",
];

const getItemLabel = (item) => {
    if (item.isOther) return "Other";
    if (item.symbol) return String(item.symbol).toUpperCase();
    return item.name || item.asset || "Asset";
};

const AllocationBar = ({
    items = [],
    total = 0,
    title = "Portfolio allocation",
}) => {
    const activeItems = (items || [])
        .filter((item) => (Number(item?.currentValue) || 0) > 0)
        .sort(
            (a, b) =>
                (Number(b.currentValue) || 0) - (Number(a.currentValue) || 0)
        );

    const computedTotal =
        Number(total) > 0
            ? Number(total)
            : activeItems.reduce(
                  (sum, item) => sum + (Number(item.currentValue) || 0),
                  0
              );

    if (activeItems.length === 0 || computedTotal <= 0) {
        return null;
    }

    const topFive = activeItems.slice(0, 5);
    const remainder = activeItems.slice(5);
    const remainderValue = remainder.reduce(
        (sum, item) => sum + (Number(item.currentValue) || 0),
        0
    );

    const segments = [...topFive];
    if (remainderValue > 0) {
        segments.push({
            name: "Other",
            symbol: "Other",
            currentValue: remainderValue,
            isOther: true,
        });
    }

    return (
        <div
            style={{
                backgroundColor: Tokens.card,
                border: `1px solid ${Tokens.hairline}`,
                borderRadius: Tokens.radiusCard,
                padding: "16px 20px",
                marginBottom: "20px",
            }}
        >
            <div
                style={{
                    color: Tokens.textSecondary,
                    fontWeight: 500,
                    fontSize: "13px",
                    marginBottom: "12px",
                }}
            >
                {title}
            </div>
            <div
                style={{
                    display: "flex",
                    width: "100%",
                    height: "8px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    backgroundColor: Tokens.raised,
                    marginBottom: "12px",
                    gap: "2px",
                }}
            >
                {segments.map((segment, i) => {
                    const val = Number(segment.currentValue) || 0;
                    const pct =
                        computedTotal > 0 ? (val / computedTotal) * 100 : 0;
                    const label = getItemLabel(segment);
                    const segKey = segment.uid || segment.id || label;
                    return (
                        <div
                            key={`bar-${segKey}`}
                            style={{
                                width: `${Math.max(pct, 2)}%`,
                                backgroundColor:
                                    BAR_PALETTE[i % BAR_PALETTE.length],
                                height: "100%",
                            }}
                        />
                    );
                })}
            </div>
            <div
                style={{
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                {segments.map((segment, i) => {
                    const val = Number(segment.currentValue) || 0;
                    const pct =
                        computedTotal > 0
                            ? ((val / computedTotal) * 100).toFixed(1)
                            : "0.0";
                    const label = getItemLabel(segment);
                    const segKey = segment.uid || segment.id || label;
                    return (
                        <div
                            key={`legend-${segKey}`}
                            title={segment.name || label}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <span
                                style={{
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "2px",
                                    backgroundColor:
                                        BAR_PALETTE[i % BAR_PALETTE.length],
                                }}
                            />
                            <Text
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "13px",
                                    fontWeight: 500,
                                }}
                            >
                                {label}
                            </Text>
                            <Text
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "12px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {pct}%
                            </Text>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AllocationBar;
